import { MutationDefinition } from '@reduxjs/toolkit/query'

import { baseQueryWithNotifications } from '@redux/api/config'
import { fetchContextMenuConfigsByIds } from '@redux/api/helpers/fetchContextMenuConfigsByIds'

import { URLS } from '@constants'
import { ActionRow, TimeLineDTOType } from '@types'

import { fetchTooltipConfigsFromActions } from './fetchTooltipConfigsFromActions'

type FetchTimelineConfigFn = MutationDefinition<
  number | string,
  typeof baseQueryWithNotifications,
  string,
  TimeLineDTOType,
  'commonApi'
>['queryFn']

export const fetchEnrichedTimelineConfig: NonNullable<FetchTimelineConfigFn> = async (
  id,
  api,
  _,
  baseQuery
) => {
  const queryResult = await baseQuery({
    url: URLS.FRONT_CFG + `/timetable/id/${id}`,
    method: 'GET',
  })

  if (queryResult.error) {
    return { error: queryResult.error }
  }

  const configData = queryResult.data as TimeLineDTOType

  const allActions = [
    ...(configData.actions || []),
    ...(configData.objectY?.actions || []),
    ...(configData.objectX?.actions || []),
  ]

  const contextMenuIds = Array.from(
    new Set(
      allActions
        ?.filter(action => action.contextMenuId)
        .map(action => action.contextMenuId as number)
    )
  )

  const [tooltips, contextMenus] = await Promise.all([
    fetchTooltipConfigsFromActions({ actions: allActions, api }),
    fetchContextMenuConfigsByIds({ contextMenuIds, api }),
  ])

  const enrichActions = (actions?: ActionRow[]) => {
    return actions?.map(action => {
      return {
        ...action,
        tooltip: tooltips.has(action.objectTooltipId)
          ? tooltips.get(action.objectTooltipId)
          : undefined,
        contextMenu: contextMenus.has(action.contextMenuId)
          ? contextMenus.get(action.contextMenuId)
          : undefined,
      }
    })
  }

  const enrichedTimeline: TimeLineDTOType = {
    ...configData,
    actions: enrichActions(configData.actions) || [],
    objectY: {
      ...configData.objectY,
      actions: enrichActions(configData.objectY?.actions),
    },
    objectX: {
      ...configData.objectX,
      actions: enrichActions(configData.objectX?.actions),
    },
  }

  return { data: enrichedTimeline }
}
